import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Hanko } from '@teamhanko/hanko-frontend-sdk';

export type Logout = (url?: string) => void;
export type Login = (path?: string) => void;
export type HankoLoginCallback = () => void;
const LOGIN_PATH = '/login';

export interface Auth {
  logout: Logout;
  login: Login;
  hanko?: Hanko;
  hankoLoginCallback: HankoLoginCallback;
  jwt?: string;
}

export const AuthContext = createContext<Auth>({
  logout: () => {
    /* no initialized */
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  login: (path?: string) => {
    /* no initialized */
  },
  hanko: undefined,
  hankoLoginCallback: () => {
    /* no initialized */
  },
  jwt: undefined,
});

interface AuthProviderProps {
  children: React.ReactNode;
  authUrl: string;
  authCookie?: string;
  onLogout: () => void;
  useNavigate: () => (path: string) => void;
}

const useAuthContext = (): Auth => useContext(AuthContext);

function AuthProvider({
  children,
  authUrl,
  onLogout,
  authCookie = 'hanko',
  useNavigate,
}: AuthProviderProps) {
  const [jwt, setJwt] = useState<string | undefined>(Cookies.get(authCookie));
  const navigate = useNavigate();
  const hanko = useMemo(
    () =>
      new Hanko(authUrl, {
        cookieName: authCookie,
        timeout: 13000,
      }),
    [authUrl, authCookie],
  );

  const [redirectPath, setRedirectPath] = useState<string>();

  // Set up event listeners for the new SDK
  useEffect(() => {
    const removeSessionCreatedListener = hanko.onSessionCreated(() => {
      setJwt(Cookies.get(authCookie));
    });

    const removeSessionExpiredListener = hanko.onSessionExpired(() => {
      setJwt(undefined);
      onLogout();
      navigate(LOGIN_PATH);
    });

    const removeUserLoggedOutListener = hanko.onUserLoggedOut(() => {
      setJwt(undefined);
      onLogout();
      navigate(LOGIN_PATH);
    });

    const removeUserDeletedListener = hanko.onUserDeleted(() => {
      setJwt(undefined);
      onLogout();
      navigate(LOGIN_PATH);
    });

    return () => {
      removeSessionCreatedListener();
      removeSessionExpiredListener();
      removeUserLoggedOutListener();
      removeUserDeletedListener();
    };
  }, [hanko, authCookie, onLogout, navigate]);

  const logout: Logout = async (path = LOGIN_PATH) => {
    console.log('logging out');
    try {
      await hanko.user.logout();
    } catch (error) {
      console.warn('Logout error:', error);
      // Fallback: manually remove cookie if logout fails
      Cookies.remove(authCookie);
    }
    setJwt(undefined);
    onLogout();
    navigate(path);
  };

  const login: Login = (redirectPath = '/') => {
    setRedirectPath(redirectPath);
    navigate(LOGIN_PATH);
  };

  const hankoLoginCallback: HankoLoginCallback = () => {
    setJwt(Cookies.get(authCookie));
    if (redirectPath) {
      navigate(redirectPath);
      setRedirectPath(undefined);
    }
  };

  const auth: Auth = { logout, login, hanko, jwt, hankoLoginCallback };
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export { useAuthContext, AuthProvider };
